import { useState } from 'react';
import {
  Modal,
  ModalContent,
  Button,
  Input,
  Control,
  Label,
  TextArea,
  LockIcon,
} from '@forge/common';
import { useApiMutation } from '@/hooks/useApiMutation';
import { sftpUsersApi } from '@/api/sftpUsers';
import type { ProvisionSftpUserPayload } from '@/api/sftpUsers';
import type { Client } from '@/api/clients';

interface ProvisionSftpUserDialogProps {
  client: Client;
  onClose: () => void;
  onSuccess: () => void;
}

const ProvisionSftpUserDialog = ({ client, onClose, onSuccess }: ProvisionSftpUserDialogProps) => {
  const [username, setUsername] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [homeDirectory, setHomeDirectory] = useState('');

  const { mutate: provision, isSubmitting } = useApiMutation<ProvisionSftpUserPayload, void>({
    mutationFn: async (data) => {
      await sftpUsersApi.provision(data);
    },
    successMessage: 'SFTP access provisioned successfully',
    errorMessage: 'Failed to provision SFTP access',
    onSuccess: () => onSuccess(),
  });

  // OpenSSH public keys start with a known algorithm prefix; guard against
  // someone pasting a private key or an unrelated blob.
  const looksLikePublicKey = /^(ssh-rsa|ssh-ed25519|ecdsa-sha2-\S+)\s+\S+/.test(publicKey.trim());
  const usernameValid = /^[a-z0-9._-]+$/i.test(username.trim());
  const isValid = usernameValid && looksLikePublicKey;

  const handleSubmit = async () => {
    if (!isValid) return;
    await provision({
      clientId: client.id,
      username: username.trim(),
      publicKey: publicKey.trim(),
      homeDirectory: homeDirectory.trim() || undefined,
    });
  };

  return (
    <Modal
      title="Provision SFTP Access"
      onModalClose={onClose}
      onBackdropClick={onClose}
      footerLeft={
        <Button variant="tertiary" onClick={onClose}>
          Cancel
        </Button>
      }
      footerRight={
        <Button
          variant="primary"
          icon={<LockIcon />}
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
        >
          {isSubmitting ? 'Provisioning...' : 'Provision Access'}
        </Button>
      }
    >
      <ModalContent padded>
        <div className="flex flex-col gap-3">
          <p className="text-sm text-color-secondary">
            Grant <span className="font-bold">{client.name}</span> SFTP access. They authenticate
            by SSH public key — no portal login is created.
          </p>

          <Control hideSpacing="bottom">
            <Label htmlFor="sftp-username">SFTP Username</Label>
            <Input
              id="sftp-username"
              placeholder="e.g., jcpl"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
            {username.trim() && !usernameValid && (
              <p className="mt-1 text-xs text-color-error">
                Only letters, numbers, dot, underscore and hyphen are allowed.
              </p>
            )}
          </Control>

          <Control hideSpacing="bottom">
            <Label htmlFor="sftp-public-key">SSH Public Key</Label>
            <TextArea
              id="sftp-public-key"
              rows={4}
              placeholder="ssh-ed25519 AAAA... user@host"
              value={publicKey}
              onChange={(e) => setPublicKey(e.target.value)}
              required
            />
            {publicKey.trim() && !looksLikePublicKey && (
              <p className="mt-1 text-xs text-color-error">
                This does not look like an OpenSSH public key. Paste the contents of the
                client&apos;s <span className="font-mono">.pub</span> file, not a private key.
              </p>
            )}
          </Control>

          <Control hideSpacing="bottom">
            <Label htmlFor="sftp-home">Home Directory (Optional)</Label>
            <Input
              id="sftp-home"
              placeholder="e.g., /jcpl"
              value={homeDirectory}
              onChange={(e) => setHomeDirectory(e.target.value)}
            />
          </Control>

          <p className="text-xs text-color-tertiary">
            The public key is stored against this client&apos;s SFTP credential. Only the
            client holds the matching private key.
          </p>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default ProvisionSftpUserDialog;
